import styles from './styles.module.scss';

import {
    SchoolClassOptions,
    SchoolClass,
    User,
    Student,
    AcademicUser,
    SchoolClassLevel,
    SchoolCourse,
    School
} from '../../../../../../types';
import { Input } from '../../../../../../components/Input';
import { FormControl } from '../../../../../../components/FormControl';
import { Select, SelectOption } from '../../../../../../components/Select';
import { useContext, useEffect, useState } from 'react';
import { Button } from '../../../../../../components/Button';
import {
    getHighestAcademicUserPosition,
    getYearOptions,
    hasRoles
} from '../../../../../../globalFunctions';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import axios, { AxiosRequestConfig } from 'axios';
import { isEqual } from 'lodash';
import { MultipleSelect } from '../../../../../../components/MultipleSelect';
import { TokenItem } from '../../../../../../components/Token';

interface AcademicTokenItem extends TokenItem {
    position?: number | string;
}

type LogicManagerClassDetailProps = {
    schoolClass: SchoolClass | undefined;
    setSchoolClass: React.Dispatch<React.SetStateAction<SchoolClass | undefined>>;
    options: SchoolClassOptions;
    onSave: React.MouseEventHandler<HTMLButtonElement>;
    onAdd: React.MouseEventHandler<HTMLButtonElement>;
    onDelete: React.MouseEventHandler<HTMLButtonElement>;
    userAuth: User;
};

export const LogicManagerClassDetail = ({
    schoolClass,
    setSchoolClass,
    options,
    onSave,
    onAdd,
    onDelete,
    userAuth
}: LogicManagerClassDetailProps) => {
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const isTeacher = hasRoles(['TEACHER'], userAuth);

    // Academic user options
    const [students, setStudents] = useState<SelectOption[]>([]);
    const [teachers, setTeachers] = useState<SelectOption[]>([]);
    const [selectedStudents, setSelectedStudents] = useState<AcademicTokenItem[]>([]);
    const [selectedTeachers, setSelectedTeachers] = useState<AcademicTokenItem[]>([]);

    // Students positions
    let studentsCounter = 0;
    let biggestPosition = getHighestAcademicUserPosition(selectedStudents);

    const onFormControlChange = (event: any, prop: string) => {
        if (schoolClass) {
            switch (prop) {
                case 'level':
                    schoolClass.level = {
                        id: parseInt(event.target.value)
                    } as SchoolClassLevel;
                    break;

                case 'course':
                    schoolClass.course = {
                        id: parseInt(event.target.value)
                    } as SchoolCourse;
                    break;

                case 'school':
                    if (event.target.value == -1) {
                        schoolClass.course.name = '';
                        schoolClass.course.slug = '';
                    }

                    schoolClass.school = {
                        id: parseInt(event.target.value)
                    } as School;
                    break;

                case 'courseName':
                    schoolClass.course.name = event.target.value;
                    break;

                case 'courseSlug':
                    schoolClass.course.slug = event.target.value;
                    break;

                default:
                    schoolClass[prop] = event.target.value;
            }

            setSchoolClass({ ...schoolClass });
        }
    };

    const onFormControlStudentChange = (event: any, student: AcademicTokenItem) => {
        const currentSelectedStudents: AcademicTokenItem[] = [];

        selectedStudents.forEach(selectedStudent => {
            if (selectedStudent.key === student.key) {
                currentSelectedStudents.push({
                    key: student.key,
                    value: student.value,
                    text: student.text,
                    description: student.description,
                    position:
                        event.target.value.trim() !== ''
                            ? !isNaN(event.target.value)
                                ? parseInt(event.target.value)
                                : ''
                            : '',
                    extras: student.extras
                });
            } else {
                currentSelectedStudents.push(selectedStudent);
            }
        });

        setSelectedStudents(currentSelectedStudents);
    };

    const removeAcademicUser = (academicUser: AcademicTokenItem, model: string) => {
        if (model === 'student') {
            const currentSelectedStudents = selectedStudents.filter(student => {
                return student.key !== academicUser.key;
            });

            setSelectedStudents(currentSelectedStudents);
        } else {
            const currentSelectedTeachers = selectedTeachers.filter(teacher => {
                return teacher.key !== academicUser.key;
            });

            setSelectedTeachers(currentSelectedTeachers);
        }
    };

    const renderTeachers = () => {
        const teachers: JSX.Element[] = [];

        const selectedTeachersMock: AcademicTokenItem[] = selectedTeachers.map(teacherMock => {
            return { ...teacherMock };
        });
        selectedTeachersMock.sort((teacher1, teacher2) =>
            teacher1.text.localeCompare(teacher2.text)
        );

        selectedTeachersMock.forEach(student => {
            teachers.push(renderTeacher(student));
        });

        return teachers;
    };

    const renderTeacher = (teacher: AcademicTokenItem) => {
        const isActiveText = teacher.description ? <strong>({teacher.description})</strong> : '';
        const lastLoginText = teacher.extras ? (
            teacher.extras['last_login'] !== '' ? (
                <em>Último acesso em: {teacher.extras['last_login']}</em>
            ) : (
                <em>Sem dados de acesso</em>
            )
        ) : (
            ''
        );

        return (
            <div
                key={`academicUserItem-teacher-${teacher.key}-${schoolClass?.id}`}
                className={styles.academicUserItem}
            >
                <FormControl
                    key={`formControl-teacher-${teacher.key}-${schoolClass?.id}`}
                    wrapperClass={styles.formControlPosition}
                    flexDirection='row-reverse'
                    label={
                        <div className={styles.academicUserItemGroup}>
                            {teacher.extras && teacher.extras['image'] && (
                                <img
                                    className={styles.profileImage}
                                    src={teacher.extras['image'].url}
                                    alt={`Imagem de perfil de ${teacher.text}`}
                                />
                            )}
                            <span>
                                {teacher.text} {isActiveText} - {lastLoginText}
                            </span>
                        </div>
                    }
                    inputId={`input-teacher-${teacher.key}-${schoolClass?.id}`}
                />

                {!(isTeacher && userAuth.id === teacher.key) && (
                    <Button type='error' title='Desvincular'>
                        <div
                            className='fa fa-times'
                            aria-hidden='true'
                            onClick={() => removeAcademicUser(teacher, 'teacher')}
                        />
                    </Button>
                )}
            </div>
        );
    };

    const renderStudents = () => {
        const students: JSX.Element[] = [];

        const selectedStudentsMock: AcademicTokenItem[] = selectedStudents.map(studentMock => {
            return { ...studentMock };
        });

        selectedStudentsMock.sort((student1, student2) => {
            if (student1.position && student2.position) {
                if (student1.position === student2.position) {
                    return 0;
                } else {
                    return student1.position > student2.position ? 1 : -1;
                }
            } else {
                return 0;
            }
        });

        const currentSelectedStudents: AcademicTokenItem[] = [];
        let foundNewStudent = false;

        selectedStudentsMock.forEach(student => {
            studentsCounter++;

            if (student.position === undefined) {
                student.position = ++biggestPosition;

                if (!foundNewStudent) {
                    foundNewStudent = true;
                }
            }

            currentSelectedStudents.push({ ...student });
            students.push(renderStudent(student));
        });

        if (foundNewStudent) {
            setSelectedStudents(currentSelectedStudents);
        }

        return students;
    };

    const renderStudent = (student: AcademicTokenItem) => {
        const isActiveText = student.description ? <strong>({student.description})</strong> : '';
        const lastLoginText = student.extras ? (
            student.extras['last_login'] !== '' ? (
                <em>Último acesso em: {student.extras['last_login']}</em>
            ) : (
                <em>Sem dados de acesso</em>
            )
        ) : (
            ''
        );

        return (
            <div
                key={`academicUserItem-student-${student.key}-${schoolClass?.id}`}
                className={styles.academicUserItem}
            >
                <FormControl
                    key={`formControl-student-${student.key}-${schoolClass?.id}`}
                    wrapperClass={styles.formControlPosition}
                    flexDirection='row-reverse'
                    label={
                        <div className={styles.academicUserItemGroup}>
                            {student.extras && student.extras['image'] && (
                                <img
                                    className={styles.profileImage}
                                    src={student.extras['image'].url}
                                    alt={`Imagem de perfil de ${student.text}`}
                                />
                            )}
                            <span>
                                {student.text} {isActiveText} - {lastLoginText}
                            </span>
                        </div>
                    }
                    inputId={`input-student-${student.key}-${schoolClass?.id}`}
                >
                    <Input
                        key={`input-student-${student.key}-${schoolClass?.id}`}
                        inputWrapperClass={`${styles.field} ${styles.position}`}
                        id={`input-student-${student.key}-${schoolClass?.id}`}
                        type='number'
                        value={student.position}
                        onChange={(e: any) => onFormControlStudentChange(e, student)}
                    />
                </FormControl>

                <Button type='error' title='Desvincular'>
                    <div
                        className='fa fa-times'
                        aria-hidden='true'
                        onClick={() => removeAcademicUser(student, 'student')}
                    />
                </Button>
            </div>
        );
    };

    const loadAcademicUsers = async (idSchool: number) => {
        try {
            const requestConfig: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    Authorization: `Bearer ${userAuth.token}`,
                    Accept: 'application/json'
                }
            };

            const responseStudents = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/options/students/${idSchool}`,
                requestConfig
            );

            setStudents(responseStudents.data);

            const currentSelectedStudents: AcademicTokenItem[] = [];

            schoolClass?.students.forEach((selectedStudent: Student) => {
                responseStudents.data.forEach((student: SelectOption) => {
                    if (student.value === selectedStudent.id) {
                        currentSelectedStudents.push({
                            key: selectedStudent.idUser,
                            value: selectedStudent.id,
                            text: selectedStudent.name,
                            description: !selectedStudent.is_active ? 'Desativado' : '',
                            position: selectedStudent.position,
                            extras: {
                                last_login: selectedStudent.last_login,
                                image: selectedStudent.image
                            }
                        });
                    }
                });
            });

            setSelectedStudents(currentSelectedStudents);

            const responseTeachers = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/options/teachers/${idSchool}`,
                requestConfig
            );

            setTeachers(responseTeachers.data);

            const currentSelectedTeachers: AcademicTokenItem[] = [];
            let renderedThisTeacher = false;

            schoolClass?.teachers.forEach((selectedTeacher: AcademicUser) => {
                responseTeachers.data.forEach((teacher: SelectOption) => {
                    if (teacher.value === selectedTeacher.id) {
                        if (teacher.key === userAuth.id) {
                            renderedThisTeacher = true;
                        }

                        currentSelectedTeachers.push({
                            key: selectedTeacher.idUser,
                            value: selectedTeacher.id,
                            text: selectedTeacher.name,
                            description: !selectedTeacher.is_active ? 'Desativado' : '',
                            extras: {
                                last_login: selectedTeacher.last_login,
                                image: selectedTeacher.image
                            }
                        });
                    }
                });
            });

            if (!renderedThisTeacher) {
                responseTeachers.data.forEach((teacher: SelectOption) => {
                    if (isTeacher && teacher.key === userAuth.id) {
                        currentSelectedTeachers.push({
                            key: teacher.key,
                            value: teacher.value,
                            text: teacher.text,
                            description: teacher.description,
                            extras: {
                                last_login: teacher.extras ? teacher.extras['last_login'] : '',
                                image: teacher.extras ? teacher.extras['image'] : null
                            }
                        });
                    }
                });
            }

            setSelectedTeachers(currentSelectedTeachers);
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: 'Erro ao buscar alunos e professores da instituição de ensino'
            });
            setOpenMessage(true);
        }
    };

    useEffect(() => {
        if (schoolClass) {
            const students: AcademicTokenItem[] = [];

            schoolClass.students.forEach(student => {
                students.push({
                    key: student.idUser,
                    value: student.id,
                    text: student.name,
                    description: !student.is_active ? 'Desativado' : '',
                    position: student.position,
                    extras: {
                        last_login: student.last_login,
                        image: student.image
                    }
                });
            });

            setSelectedStudents(students);

            const teachers: AcademicTokenItem[] = [];

            schoolClass.teachers.forEach(teacher => {
                teachers.push({
                    key: teacher.idUser,
                    value: teacher.id,
                    text: teacher.name,
                    description: !teacher.is_active ? 'Desativado' : '',
                    extras: {
                        last_login: teacher.last_login,
                        image: teacher.image
                    }
                });
            });

            setSelectedTeachers(teachers);
        }
    }, [schoolClass?.id]);

    useEffect(() => {
        if (schoolClass) {
            if (schoolClass.school.id !== 0) {
                loadAcademicUsers(schoolClass.school.id);
            } else {
                setSelectedStudents([]);
                setSelectedTeachers([]);

                setStudents([]);
                setTeachers([]);

                setMessage({
                    type: 'info',
                    content:
                        'Informe uma instituição para que os professores e alunos sejam carregados'
                });
                setOpenMessage(true);
            }
        }
    }, [schoolClass?.school]);

    useEffect(() => {
        if (schoolClass) {
            const currentStudents =
                selectedStudents.length === 0
                    ? []
                    : selectedStudents.map(student => {
                          return {
                              id: student.value,
                              idUser: student.key,
                              name: student.text,
                              position: student.position,
                              is_active: student.description === '' ? true : false,
                              last_login: student.extras ? student.extras['last_login'] : '',
                              image: student.extras ? student.extras['image'] : null
                          } as Student;
                      });

            if (schoolClass.students.length !== currentStudents.length) {
                schoolClass.students = currentStudents;
                setSchoolClass({ ...schoolClass });
            }
        }
    }, [selectedStudents]);

    useEffect(() => {
        if (schoolClass) {
            const currentTeachers =
                selectedTeachers.length === 0
                    ? []
                    : selectedTeachers.map(teacher => {
                          return {
                              id: teacher.value,
                              idUser: teacher.key,
                              name: teacher.text,
                              is_active: teacher.description === '' ? true : false,
                              last_login: teacher.extras ? teacher.extras['last_login'] : '',
                              image: teacher.extras ? teacher.extras['image'] : null
                          } as AcademicUser;
                      });

            if (schoolClass.teachers.length !== currentTeachers.length) {
                schoolClass.teachers = currentTeachers;
                setSchoolClass({ ...schoolClass });
            }
        }
    }, [selectedTeachers]);

    return (
        <div className={styles.wrapper}>
            {schoolClass ? (
                <>
                    <div className={styles.btnBox}>
                        <Button type='success' title='Adicionar' onClick={onAdd}>
                            <div className='fa fa-plus' aria-hidden='true' />
                        </Button>

                        <div className={styles.btnBoxGroup}>
                            {schoolClass.id > 0 && (
                                <Button type='error' title='Excluir' onClick={onDelete}>
                                    <div className='fa fa-trash' aria-hidden='true' />
                                </Button>
                            )}
                            <Button type='success' title='Salvar' onClick={onSave}>
                                <div className='fa fa-check' aria-hidden='true' />
                            </Button>
                        </div>
                    </div>

                    <div className={styles.container}>
                        <div className={styles.row}>
                            <FormControl
                                key='formControlName'
                                flexDirection='column'
                                wrapperClass={styles.five}
                                label='Nome'
                                inputId='name'
                            >
                                <Input
                                    key='inputName'
                                    inputWrapperClass={styles.field}
                                    id='name'
                                    type='text'
                                    value={schoolClass.name}
                                    onChange={(e: any) => onFormControlChange(e, 'name')}
                                />
                            </FormControl>

                            <FormControl
                                key='formControlLevel'
                                flexDirection='column'
                                wrapperClass={styles.two}
                                label='Série'
                                inputId='level'
                            >
                                <Select
                                    key='selectLevel'
                                    selectWrapperClass={styles.field}
                                    id='level'
                                    options={[{ key: 'empty', value: 0, text: '' }].concat(
                                        options.levels
                                    )}
                                    value={schoolClass.level ? schoolClass.level.id : 0}
                                    onChange={(e: any) => onFormControlChange(e, 'level')}
                                />
                            </FormControl>

                            <FormControl
                                key='formControlYear'
                                flexDirection='column'
                                wrapperClass={styles.two}
                                label='Ano letivo'
                                inputId='year'
                            >
                                <Select
                                    key='selectYear'
                                    selectWrapperClass={styles.field}
                                    id='year'
                                    options={getYearOptions(schoolClass.year)}
                                    showEmptyOption={true}
                                    value={schoolClass.year}
                                    onChange={(e: any) => onFormControlChange(e, 'year')}
                                />
                            </FormControl>
                        </div>

                        <div className={styles.row}>
                            <FormControl
                                key='formControlCourse'
                                flexDirection='column'
                                wrapperClass={styles.three}
                                label='Curso'
                                inputId='course'
                            >
                                <Select
                                    key='selectCourse'
                                    selectWrapperClass={styles.field}
                                    id='year'
                                    options={[{ key: 'empty', value: 0, text: '' }].concat(
                                        options.courses.concat([
                                            { key: 'other', value: -1, text: 'Outro' }
                                        ])
                                    )}
                                    value={schoolClass.course ? schoolClass.course.id : 0}
                                    onChange={(e: any) => onFormControlChange(e, 'course')}
                                />
                            </FormControl>

                            {schoolClass.course.id === -1 && (
                                <>
                                    <FormControl
                                        key='formControlCourseName'
                                        flexDirection='column'
                                        wrapperClass={styles.three}
                                        label='Nome do curso'
                                        inputId='courseName'
                                    >
                                        <Input
                                            key='inputCourseName'
                                            inputWrapperClass={styles.field}
                                            id='courseName'
                                            type='text'
                                            value={
                                                schoolClass.course ? schoolClass.course.name : ''
                                            }
                                            onChange={(e: any) =>
                                                onFormControlChange(e, 'courseName')
                                            }
                                        />
                                    </FormControl>

                                    <FormControl
                                        key='formControlCourseSlug'
                                        flexDirection='column'
                                        wrapperClass={styles.one}
                                        label='Sigla do curso'
                                        inputId='courseSlug'
                                    >
                                        <Input
                                            key='inputCourseSlug'
                                            inputWrapperClass={styles.field}
                                            id='courseSlug'
                                            type='text'
                                            value={
                                                schoolClass.course ? schoolClass.course.slug : ''
                                            }
                                            onChange={(e: any) =>
                                                onFormControlChange(e, 'courseSlug')
                                            }
                                        />
                                    </FormControl>
                                </>
                            )}
                        </div>

                        <div className={styles.row}>
                            <FormControl
                                key='formControlSchool'
                                flexDirection='column'
                                wrapperClass={styles.five}
                                label='Instituição'
                                inputId='school'
                            >
                                <Select
                                    key='selectSchool'
                                    selectWrapperClass={styles.field}
                                    id='school'
                                    options={[{ key: 'empty', value: 0, text: '' }].concat(
                                        options.schools
                                    )}
                                    value={schoolClass.school ? schoolClass.school.id : 0}
                                    onChange={(e: any) => onFormControlChange(e, 'school')}
                                />
                            </FormControl>
                        </div>

                        <div className={styles.row}>
                            <MultipleSelect
                                key='multipleSelectTeachers'
                                id='multipleSelectTeachers'
                                options={teachers}
                                selectedOptions={selectedTeachers}
                                setSelectedOptions={setSelectedTeachers}
                                showTokenizer={false}
                                placeholder='Professores'
                                title='Vincular professores'
                            />
                        </div>

                        {selectedTeachers.length > 0 && (
                            <div className={styles.academicUserList}>{renderTeachers()}</div>
                        )}

                        <div className={styles.row}>
                            <MultipleSelect
                                key='multipleSelectStudents'
                                id='multipleSelectStudents'
                                options={students}
                                selectedOptions={selectedStudents}
                                setSelectedOptions={setSelectedStudents}
                                showTokenizer={false}
                                placeholder='Alunos'
                                title='Vincular alunos'
                            />
                        </div>

                        {selectedStudents.length > 0 && (
                            <div className={styles.academicUserList}>{renderStudents()}</div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.btnBox}>
                        <Button type='success' title='Adicionar' onClick={onAdd}>
                            <div className='fa fa-plus' aria-hidden='true' />
                        </Button>
                    </div>

                    <div className={styles.noSelected}>Selecione uma turma</div>
                </>
            )}
        </div>
    );
};
